import type { PlayerTechState, TechUpgradeId } from "../../../../shared/types/BattleEvent";

const techOrder: TechUpgradeId[] = ["infantry_drills", "arcane_focus", "siege_engineering", "fortification_plans"];
const BASE_COST = 160;
const COST_STEP = 55;
const MAX_TECHS = 4;

export class TechSystem {
  private readonly researchedTechs = new Set<TechUpgradeId>();
  // War Funds "Foresight" upgrade: 0..0.45 fraction off every tech.
  private readonly discount: number;

  constructor(discount = 0) {
    this.discount = Math.max(0, Math.min(0.45, discount));
  }

  get researched(): TechUpgradeId[] {
    return techOrder.filter((tech) => this.researchedTechs.has(tech));
  }

  get maxReached() {
    return this.researchedTechs.size >= MAX_TECHS;
  }

  costFor(tech: TechUpgradeId) {
    if (this.researchedTechs.has(tech) || this.maxReached) return Infinity;
    const full = BASE_COST + this.researchedTechs.size * COST_STEP;
    return Math.round(full * (1 - this.discount));
  }

  research(tech: TechUpgradeId) {
    if (this.researchedTechs.has(tech) || this.maxReached) return false;
    this.researchedTechs.add(tech);
    return true;
  }

  has(tech: TechUpgradeId) {
    return this.researchedTechs.has(tech);
  }

  get state(): PlayerTechState {
    const nextCosts: PlayerTechState["nextCosts"] = {};
    for (const tech of techOrder) {
      const cost = this.costFor(tech);
      if (Number.isFinite(cost)) nextCosts[tech] = cost;
    }
    return {
      researched: this.researched,
      nextCosts,
      maxReached: this.maxReached
    };
  }
}

export const techIds = techOrder;
